<template>
  <div>
    <product-search
      :params="{ department: order.department_id }"
      :open.sync="searching"
    ></product-search>
    <add-product :order="order"></add-product>

    <div class="order-detail">
      <div is="order" inline-template :order.sync="order">
        <section class="row" id="order-heading">
          <div class="col-xs-12">
            <p class="pull-right pickup-time">
              <b>Pickup Time:</b> {{ order.pickup_at | date-format 'long' }}
            </p>

            <h2>
              <order-icon-image
                class="icon-lg"
                :order="order"
              ></order-icon-image>
              Order {{ order.number }}
              <small class="label label-success" v-if="order.source == 'kiosk'"
                >KIOSK</small
              >
              <small class="label label-danger" v-if="overdue">PAST DUE</small>
              <small class="label label-info" v-if="completed">COMPLETE</small>
              <small class="label label-default" v-if="label_printed"
                >LABEL PRINTED</small
              >
            </h2>
          </div>
        </section>
        <div class="row">
          <div class="col-xs-12">
            <div class="alert alert-info" role="alert" v-if="completed">
              This order was completed on
              <b>{{ order.completed_at | date-format 'long' }}</b>
            </div>
          </div>
        </div>
        <section class="row">
          <div class="col-xs-12">
            <table class="table">
              <tr>
                <th>Customer Name</th>
                <th>Contact #</th>
                <th>Total Items</th>
                <th>Substitutions</th>
                <th>Total $ <small>(estimate)</small></th>
                <th>Weight</th>
              </tr>
              <tr>
                <td>{{ customer.first_name }} {{{ last_name }}}</td>

                <td v-if="!customer.phone"><em class="missing">None</em></td>
                <td v-if="customer.phone">{{ customer.phone | phone }}</td>

                <td>{{ items.length }}</td>
                <td><b v-if="!order.substitutions_allowed">Not</b> Allowed</td>
                <td>{{ subtotal | currency }}</td>
                <td>
                  {{ weight | fractional }}
                  {{ 'pound' | unit-of-measure weight true }}
                </td>
              </tr>
            </table>

            <div class="well" v-if="order.notes">
              <b>Notes:</b> {{ order.notes }}
            </div>
          </div>
        </section>
      </div>

      <div v-if="order.view_order_production" class="view-production-warning">
        ***Customer requested to view this order being weighed***
      </div>

      <section class="row">
        <div class="col-xs-12">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>
                  Item
                  <button
                    class="btn btn-link btn-xs add-item item-link"
                    v-if="can_add_items"
                    @click.prevent.stop="searching = true"
                  >
                    <i class="fa fa-plus fa-lg"></i>
                  </button>
                </th>
                <th>&nbsp;</th>
                <th class="text-center">Processed</th>
                <th class="text-center">Completed</th>
                <th class="text-center">Out of Stock</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in items"
                :item.sync="item"
                :order.sync="order"
                is="item"
                track-by="id"
              ></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="row">
        <div class="col-xs-12 text-center order-controls">
          <button class="btn btn-lg btn-link" v-link="{ path: '/' }">
            <i class="fa fa-chevron-left"></i> Back
          </button>
          <button
            class="btn btn-lg btn-default"
            data-spinner-color="#444"
            data-style="slide-up"
            @click="printLabel()"
          >
            <span v-if="printing">
              <i class="fa fa-circle-notch fa-spin"></i>
              Printing...
            </span>
            <span v-else>
              <i class="fa fa-print"></i>
              Print
            </span>
          </button>
          <button
            class="btn btn-lg btn-default"
            v-if="hasCardAuthorization"
            @click="viewingCardActions = true"
          >
            Charges &amp; Refunds
          </button>
          <button
            class="btn btn-lg"
            :class="{
              'btn-success': can_complete,
              'btn-default': !can_complete,
            }"
            :disabled="!can_complete"
            v-if="!completed"
            @click="complete()"
          >
            Complete Order
          </button>
          <button
            class="btn btn-lg"
            :class="{
              'btn-danger': can_cancel,
              'btn-default': !can_cancel,
            }"
            :disabled="!can_cancel"
            v-if="!completed"
            @click="cancel()"
          >
            Cancel Order
          </button>
        </div>
      </section>
    </div>

    <charge-and-refund
      v-if="viewingCardActions"
      @close="viewingCardActions = false"
      :order="order"
    ></charge-and-refund>
  </div>
</template>

<script>
import { delay } from 'lodash';
import axios from 'axios';
import { Api, IO, printLabel } from 'src/utilities';
import Stash from 'src/stash';
import Order from '../Order';
import Item from '../Item';
import DescribesAnOrder from 'mixins/DescribesAnOrder';
import ProductSearch from '../modals/ProductSearch';
import AddProduct from '../modals/AddProduct';
import OrderIconImage from '../OrderIconImage';
import ChargeAndRefund from '../ChargeAndRefund';

export default {
  mixins: [DescribesAnOrder],

  route: {
    activate: transition => {
      Stash.setProduct();

      return Api.get(`orders/${transition.to.params.id}`, {
        params: { include: 'items,cardAuthorization' },
        channel: 'foreground',
      }).then(({ data }) => Stash.setOrder(transition.to.params.id, data));
    },
  },

  data() {
    return {
      searching: false,
      printing: false,
      viewingCardActions: false,
    };
  },

  attached() {
    IO.on('orders', payload => {
      if (
        Number(payload.id) === Number(this.order.id) &&
        payload.event === 'canceled'
      ) {
        // the order we're looking at just got canceled
        this.$route.router.go('/');
      }
    });
  },

  watch: {
    order(o) {
      if (!o.id) {
        // an order has been canceled and removed from the stash. redirect back
        // to the homepage.
        this.$route.router.go('/');
      }
    },
  },

  computed: {
    order() {
      return this.state.orders.find(o => o.id === +this.$route.params.id) || {};
    },

    hasCardAuthorization() {
      return this.order.cardAuthorization !== undefined;
    },
  },

  methods: {
    complete() {
      this.completed = true;

      // @api: mark an order as 'complete'
      Api.put(`orders/${this.order.id}/complete`)
        .then(() => {
          window.scrollTo(0, 0);
          this.$route.router.go('/');
        })
        .catch(() => (this.completed = false));
    },

    cancel() {
      this.canceled = true;

      // @api: mark an order as 'complete'
      Api.delete(`orders/${this.order.id}/cancel`)
        .then(() => {
          window.scrollTo(0, 0);
          this.$route.router.go('/');
        })
        .catch(() => (this.canceled = false));
    },

    printLabel() {
      this.printing = true;

      const reset = () => {
        delay(() => (this.printing = false), 400);
      };

      printLabel(this.order.id).then(reset).catch(reset);
    },
  },

  components: {
    Order,
    Item,
    ProductSearch,
    AddProduct,
    OrderIconImage,
    ChargeAndRefund,
  },
};
</script>



// WEBPACK FOOTER //
// OrderDetail.vue?1fb8c65c